
import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { BookingGeographicData } from '@/utils/booking-analytics';
import { MapPin, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface WorldMapProps {
  data: BookingGeographicData[];
}

const WorldMap: React.FC<WorldMapProps> = ({ data }) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
const [mapboxToken, setMapboxToken] = useState<string>('');
const [tokenError, setTokenError] = useState<string>('');
const [isLoadingToken, setIsLoadingToken] = useState(true);

  useEffect(() => {
    // Try to fetch token from Supabase first
    fetchMapboxToken();
  }, []);

  useEffect(() => {
    // Try to initialize map when token is available
    if (mapboxToken && mapContainer.current && !map.current) {
      initializeMap();
    }
  }, [mapboxToken, data]);

const fetchMapboxToken = async () => {
  try {
    setIsLoadingToken(true);

    const { data: tokenData, error } = await supabase.functions.invoke('get-mapbox-token');

    if (error) {
      console.error('Error fetching Mapbox token:', error);
      setTokenError(`Server error fetching Mapbox token: ${error.message || 'unknown error'}`);
    } else if (tokenData?.error) {
      console.error('Server returned error:', tokenData.error);
      setTokenError(`Server error: ${tokenData.error}`);
    } else if (tokenData?.mapboxToken) {
      setMapboxToken(tokenData.mapboxToken);
      setTokenError('');
    } else {
      setTokenError('Mapbox token not configured on server');
    }
  } catch (error: any) {
    console.error('Error fetching Mapbox token:', error);
    setTokenError(`Failed to fetch Mapbox token: ${error?.message || 'unknown error'}`);
  } finally {
    setIsLoadingToken(false);
  }
};

  const initializeMap = () => {
    if (!mapContainer.current || !mapboxToken) return;

    try {
      mapboxgl.accessToken = mapboxToken;
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        projection: 'globe' as any,
        zoom: 1.5,
        center: [30, 15],
      });

      // Add navigation controls
      map.current.addControl(
        new mapboxgl.NavigationControl({
          visualizePitch: true,
        }),
        'top-right'
      );

      map.current.on('style.load', () => {
        addDataLayer();
      });

      setTokenError('');
    } catch (error) {
      console.error('Error initializing map:', error);
      setTokenError('Invalid Mapbox token. Please check your token and try again.');
    }
  };

  const addDataLayer = () => {
    if (!map.current) return;
    
    console.log('WorldMap: Adding data layer with data:', data);
    
    if (!data || data.length === 0) {
      console.log('WorldMap: No data provided, skipping data layer');
      return;
    }

    // Create GeoJSON data from analytics data
    const geojsonData = {
      type: 'FeatureCollection' as const,
      features: data
        .filter(item => item.country !== '(not set)') // Filter out invalid countries
        .map((item) => {
          const coords = getCountryCoordinates(item.country);
          console.log('WorldMap: Processing country:', item.country, 'coords:', coords, 'data:', item);
          return {
            type: 'Feature' as const,
            geometry: {
              type: 'Point' as const,
              coordinates: coords,
            },
            properties: {
              country: item.country,
              sessions: item.sessions,
              users: item.users,
              percentage: item.percentage,
            },
          };
        }),
    };

    console.log('WorldMap: Generated GeoJSON data:', geojsonData);

    // Add source
    map.current.addSource('analytics-data', {
      type: 'geojson',
      data: geojsonData,
    });

    // Enhanced circle layer with better visibility
    map.current.addLayer({
      id: 'analytics-points',
      type: 'circle',
      source: 'analytics-data',
      paint: {
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['get', 'sessions'],
          1, 8,
          10, 15,
          50, 25,
          100, 35,
          500, 50
        ],
        'circle-color': [
          'interpolate',
          ['linear'],
          ['get', 'sessions'],
          1, '#fef3c7',
          10, '#fbbf24',
          25, '#f59e0b',
          50, '#d97706',
          100, '#92400e'
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
        'circle-opacity': 0.8
      }
    });

    // Add labels for countries with data
    map.current.addLayer({
      id: 'analytics-labels',
      type: 'symbol',
      source: 'analytics-data',
      layout: {
        'text-field': [
          'format',
          ['get', 'country'], {},
          '\n', {},
          ['get', 'sessions'], { 'font-scale': 0.8 }
        ],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-offset': [0, 0]
      },
      paint: {
        'text-color': '#374151',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1
      }
    });

    // Enhanced click events for data points
    map.current.on('click', 'analytics-points', (e) => {
      if (!e.features || !e.features[0]) return;
      
      const coordinates = (e.features[0].geometry as any).coordinates.slice();
      const properties = e.features[0].properties;

      // Ensure that if the map is zoomed out such that multiple
      // copies of the feature are visible, the popup appears
      // over the copy being pointed to.
      while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
      }

      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(
          `<div class="p-3">
            <h3 class="font-bold text-lg text-gray-800">${properties?.country}</h3>
            <p class="text-gray-600">Sessions: <span class="font-semibold">${properties?.sessions}</span></p>
            <p class="text-gray-600">Users: <span class="font-semibold">${properties?.users}</span></p>
            <p class="text-gray-600">Percentage: <span class="font-semibold">${properties?.percentage}%</span></p>
            <div class="mt-2 text-xs text-gray-500">
              ${properties?.sessions === 1 ? '1 session' : `${properties?.sessions} sessions`} from this location
            </div>
          </div>`
        )
        .addTo(map.current!);
    });

    // Enhanced hover effects
    map.current.on('mouseenter', 'analytics-points', (e) => {
      if (map.current) {
        map.current.getCanvas().style.cursor = 'pointer';
        
        // Add hover popup
        const features = e.features;
        if (features && features.length > 0) {
          const feature = features[0];
          const country = feature.properties?.country;
          const sessions = feature.properties?.sessions;

          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false
          })
            .setLngLat(e.lngLat)
            .setHTML(`<div class="text-sm"><strong>${country}</strong><br/>${sessions} bookings<br/>$${feature.properties?.totalRevenue?.toFixed(0) || 0} revenue</div>`)
            .addTo(map.current);

          // Store popup reference to remove on mouseleave
          (map.current as any)._hoverPopup = popup;
        }
      }
    });

    map.current.on('mouseleave', 'analytics-points', () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = '';
        
        // Remove hover popup
        if ((map.current as any)._hoverPopup) {
          (map.current as any)._hoverPopup.remove();
          (map.current as any)._hoverPopup = null;
        }
      }
    });
  };


  // Comprehensive country coordinates mapping with aliases
  const getCountryCoordinates = (country: string): [number, number] => {
    const coordinates: Record<string, [number, number]> = {
      'United States': [-95.7129, 37.0902],
      'US': [-95.7129, 37.0902],
      'USA': [-95.7129, 37.0902],
      'China': [104.1954, 35.8617],
      'CN': [104.1954, 35.8617],
      'Singapore': [103.8198, 1.3521],
      'SG': [103.8198, 1.3521],
      'Malaysia': [101.9758, 4.2105],
      'MY': [101.9758, 4.2105],
      'Taiwan': [120.9605, 23.6978],
      'TW': [120.9605, 23.6978],
      'Canada': [-106.3468, 56.1304],
      'CA': [-106.3468, 56.1304],
      'Hong Kong': [114.1694, 22.3193],
      'HK': [114.1694, 22.3193],
      'India': [78.9629, 20.5937],
      'IN': [78.9629, 20.5937],
      'United Kingdom': [-3.4360, 55.3781],
      'UK': [-3.4360, 55.3781],
      'GB': [-3.4360, 55.3781],
      'Britain': [-3.4360, 55.3781],
      'England': [-1.1743, 52.3555],
      'Scotland': [-4.2026, 56.4907],
      'Wales': [-3.7837, 52.1307],
      'Australia': [133.7751, -25.2744],
      'AU': [133.7751, -25.2744],
      'Germany': [10.4515, 51.1657],
      'DE': [10.4515, 51.1657],
      'Japan': [138.2529, 36.2048],
      'JP': [138.2529, 36.2048],
      'France': [2.2137, 46.2276],
      'FR': [2.2137, 46.2276],
      'Brazil': [-51.9253, -14.2350],
      'BR': [-51.9253, -14.2350],
      'South Korea': [127.7669, 35.9078],
      'KR': [127.7669, 35.9078],
      'Korea': [127.7669, 35.9078],
      'Thailand': [100.9925, 15.8700],
      'TH': [100.9925, 15.8700],
      'Philippines': [121.7740, 12.8797],
      'PH': [121.7740, 12.8797],
      'Indonesia': [113.9213, -0.7893],
      'ID': [113.9213, -0.7893],
      'Vietnam': [108.2772, 14.0583],
      'VN': [108.2772, 14.0583],
      'Netherlands': [5.2913, 52.1326],
      'NL': [5.2913, 52.1326],
      'Holland': [5.2913, 52.1326],
      'Sweden': [18.6435, 60.1282],
      'SE': [18.6435, 60.1282],
      'Norway': [8.4689, 60.4720],
      'NO': [8.4689, 60.4720],
      'Denmark': [9.5018, 56.2639],
      'DK': [9.5018, 56.2639],
      'Switzerland': [8.2275, 46.8182],
      'CH': [8.2275, 46.8182],
      'Austria': [14.5501, 47.5162],
      'AT': [14.5501, 47.5162],
      'Belgium': [4.4699, 50.5039],
      'BE': [4.4699, 50.5039],
      'Finland': [25.7482, 61.9241],
      'FI': [25.7482, 61.9241],
      'Ireland': [-8.2439, 53.4129],
      'IE': [-8.2439, 53.4129],
      'Portugal': [-8.2245, 39.3999],
      'PT': [-8.2245, 39.3999],
      'Spain': [-3.7492, 40.4637],
      'ES': [-3.7492, 40.4637],
      'Italy': [12.5674, 41.8719],
      'IT': [12.5674, 41.8719],
      'Greece': [21.8243, 39.0742],
      'GR': [21.8243, 39.0742],
      'Turkey': [35.2433, 38.9637],
      'TR': [35.2433, 38.9637],
      'Russia': [105.3188, 61.5240],
      'RU': [105.3188, 61.5240],
      'Mexico': [-102.5528, 23.6345],
      'MX': [-102.5528, 23.6345],
      'Colombia': [-74.2973, 4.5709],
      'CO': [-74.2973, 4.5709],
      'Argentina': [-63.6167, -38.4161],
      'AR': [-63.6167, -38.4161],
      'Chile': [-71.5430, -35.6751],
      'CL': [-71.5430, -35.6751],
      'Peru': [-75.0152, -9.1900],
      'PE': [-75.0152, -9.1900],
      'Venezuela': [-66.5897, 6.4238],
      'VE': [-66.5897, 6.4238],
      'Ecuador': [-78.1834, -1.8312],
      'EC': [-78.1834, -1.8312],
      'Bolivia': [-63.5887, -16.2902],
      'BO': [-63.5887, -16.2902],
      'Uruguay': [-55.7658, -32.5228],
      'UY': [-55.7658, -32.5228],
      'Paraguay': [-58.4438, -23.4425],
      'PY': [-58.4438, -23.4425],
      'South Africa': [22.9375, -30.5595],
      'ZA': [22.9375, -30.5595],
      'Egypt': [30.8025, 26.8206],
      'EG': [30.8025, 26.8206],
      'Morocco': [-7.0926, 31.7917],
      'MA': [-7.0926, 31.7917],
      'Nigeria': [8.6753, 9.0820],
      'NG': [8.6753, 9.0820],
      'Kenya': [37.9062, -0.0236],
      'KE': [37.9062, -0.0236],
      'Ghana': [-1.0232, 7.9465],
      'GH': [-1.0232, 7.9465],
      'Ethiopia': [40.4897, 9.145],
      'ET': [40.4897, 9.145],
      'Tanzania': [34.8888, -6.369],
      'TZ': [34.8888, -6.369],
      'Uganda': [32.2903, 1.3733],
      'UG': [32.2903, 1.3733],
      'Israel': [34.8516, 32.0853],
      'IL': [34.8516, 32.0853],
      'Saudi Arabia': [45.0792, 23.8859],
      'SA': [45.0792, 23.8859],
      'UAE': [53.8478, 23.4241],
      'AE': [53.8478, 23.4241],
      'Iran': [53.6880, 32.4279],
      'IR': [53.6880, 32.4279],
      'Iraq': [43.6793, 33.2232],
      'IQ': [43.6793, 33.2232],
      'Pakistan': [69.3451, 30.3753],
      'PK': [69.3451, 30.3753],
      'Bangladesh': [90.3563, 23.6850],
      'BD': [90.3563, 23.6850],
      'Sri Lanka': [80.7718, 7.8731],
      'LK': [80.7718, 7.8731],
      'Myanmar': [95.9560, 21.9162],
      'MM': [95.9560, 21.9162],
      'Cambodia': [104.9910, 12.5657],
      'KH': [104.9910, 12.5657],
      'Laos': [102.4955, 19.8563],
      'LA': [102.4955, 19.8563],
      'New Zealand': [174.8860, -40.9006],
      'NZ': [174.8860, -40.9006],
      'Poland': [19.1343, 51.9194],
      'PL': [19.1343, 51.9194],
      'Czech Republic': [15.4729, 49.8175],
      'CZ': [15.4729, 49.8175],
      'Hungary': [19.5033, 47.1625],
      'HU': [19.5033, 47.1625],
      'Romania': [24.9668, 45.9432],
      'RO': [24.9668, 45.9432],
      'Ukraine': [31.1656, 48.3794],
      'UA': [31.1656, 48.3794],
      'Slovakia': [19.699, 48.669],
      'SK': [19.699, 48.669],
      'Slovenia': [14.9955, 46.1512],
      'SI': [14.9955, 46.1512],
      'Croatia': [15.2, 45.1],
      'HR': [15.2, 45.1],
      'Serbia': [21.0059, 44.0165],
      'RS': [21.0059, 44.0165],
      'Bulgaria': [25.4858, 42.7339],
      'BG': [25.4858, 42.7339],
      'Jordan': [36.2384, 30.5852],
      'JO': [36.2384, 30.5852],
      'Lebanon': [35.8623, 33.8547],
      'LB': [35.8623, 33.8547],
      'Syria': [38.9968, 34.8021],
      'SY': [38.9968, 34.8021],
      'Afghanistan': [67.7090, 33.9391],
      'AF': [67.7090, 33.9391],
      'Algeria': [1.6596, 28.0339],
      'DZ': [1.6596, 28.0339],
      'Tunisia': [9.5375, 33.8869],
      'TN': [9.5375, 33.8869],
      'Libya': [17.2283, 26.3351],
      'LY': [17.2283, 26.3351],
      '(not set)': [0, 0], // Handle analytics "not set" values
    };
    
    console.log('WorldMap: Looking up coordinates for country:', country);
    const coords = coordinates[country] || [0, 0];
    console.log('WorldMap: Found coordinates:', coords);
    
    // If coordinates are [0, 0] and country is not "(not set)", log a warning
    if (coords[0] === 0 && coords[1] === 0 && country !== '(not set)') {
      console.warn(`WorldMap: No coordinates found for country: "${country}"`);
    }
    
    return coords;
  };

  if (isLoadingToken) {
    return (
      <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
        <Loader2 className="h-12 w-12 text-gray-400 mb-4 animate-spin" />
        <h3 className="text-lg font-semibold text-gray-600 mb-2">Loading World Map</h3>
        <p className="text-gray-500 text-center mb-4">
          Fetching Mapbox configuration...
        </p>
      </div>
    );
  }

if (!mapboxToken) {
  return (
    <div className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg">
      <MapPin className="h-12 w-12 text-gray-400 mb-4" />
      <h3 className="text-lg font-semibold text-gray-600 mb-2">World Map Visualization</h3>
      <p className="text-gray-500 text-center mb-4">
        {tokenError || 'Unable to load Mapbox. Please ensure your account has admin access and MAPBOX_API_KEY is set in Supabase.'}
      </p>
    </div>
  );
}


  return (
    <div className="relative w-full h-96">
      <div ref={mapContainer} className="absolute inset-0 rounded-lg shadow-lg" />
      
      {/* Enhanced Legend */}
      {!isLoadingToken && mapboxToken && data.length > 0 && (
        <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-sm mb-2">Session Distribution</h4>
          <div className="space-y-1 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-100 border border-amber-300"></div>
              <span>1-9 sessions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-300"></div>
              <span>10-24 sessions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span>25-49 sessions</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-700"></div>
              <span>50+ sessions</span>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2 pt-2 border-t">
            Total: {data.reduce((sum, item) => sum + item.sessions, 0)} sessions across {data.length} countries
          </div>
        </div>
      )}
      
      {process.env.NODE_ENV === 'development' && data.length > 0 && (
        <div className="absolute top-4 right-4 bg-white p-2 rounded shadow text-xs">
          <strong>Debug Info:</strong><br/>
          Countries: {data.length}<br/>
          Total Sessions: {data.reduce((sum, item) => sum + item.sessions, 0)}
        </div>
      )}
    </div>
  );
};

export default WorldMap;
